import { en_default } from './chunk-FYX64CDL.js';

// lib/format.ts
var { $meta: _meta, ...coreMessages } = en_default;
var coreLocale = coreMessages;
var CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
var LOCAL_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
var pad2 = (n) => String(n).padStart(2, "0");
function parseCalendarDate(value) {
  const m = CALENDAR_DATE.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]) ? d : null;
}
function toDate(value) {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string" && typeof value !== "number") return null;
  const s = String(value);
  const d = parseCalendarDate(s) ?? new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
function toDateInputValue(value) {
  if (!value) return "";
  if (typeof value === "string" && CALENDAR_DATE.test(value)) return value;
  const d = toDate(value);
  if (!d) return typeof value === "string" ? value : "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function toDateTimeInputValue(value) {
  if (!value) return "";
  if (typeof value === "string" && LOCAL_DATETIME.test(value)) return value;
  const d = toDate(value);
  if (!d) return typeof value === "string" ? value : "";
  return `${toDateInputValue(d)}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function formatDate(value, locale) {
  if (!value) return "";
  const d = parseCalendarDate(String(value)) ?? new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}
function formatTime(value, locale) {
  if (!value) return "";
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}
function formatDateTime(value, locale) {
  if (!value) return "";
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return `${formatDate(value, locale)} ${formatTime(value, locale)}`;
}
var DEFAULT_BOOLEAN_LABELS = { yes: coreLocale["common.yes"], no: coreLocale["common.no"] };
function asYesNo(value, labels) {
  return value === false || value === 0 || String(value) === "false" ? labels.no : labels.yes;
}
var DEFAULT_CURRENCY = "USD";
function formatValue(value, format, fmt = {}) {
  if (value === void 0 || value === null) return "";
  const { locale, currency = DEFAULT_CURRENCY, booleanLabels = DEFAULT_BOOLEAN_LABELS } = fmt;
  if (typeof value === "boolean") return asYesNo(value, booleanLabels);
  switch (format) {
    case "date":
      return formatDate(value, locale);
    case "time":
      return formatTime(value, locale);
    case "datetime":
      return formatDateTime(value, locale);
    case "currency":
      return typeof value === "number" ? new Intl.NumberFormat(locale, { style: "currency", currency }).format(value) : String(value);
    case "number":
      return typeof value === "number" ? new Intl.NumberFormat(locale).format(value) : String(value);
    case "percent":
      return typeof value === "number" ? new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(value / 100) : String(value);
    case "boolean":
      return asYesNo(value, booleanLabels);
    default:
      return String(value);
  }
}
function compareCellValues(a, b) {
  const aEmpty = a === null || a === void 0 || a === "";
  const bEmpty = b === null || b === void 0 || b === "";
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  const aTime = dateLikeTime(a);
  const bTime = dateLikeTime(b);
  if (aTime !== null && bTime !== null) return aTime - bTime;
  return String(a).localeCompare(String(b));
}
function dateLikeTime(value) {
  if (value instanceof Date) return value.getTime();
  const text = String(value);
  if (!/\d/.test(text) || !/[-/:T]/.test(text)) return null;
  const time = Date.parse(text);
  return Number.isNaN(time) ? null : time;
}
function sortRows(rows, field, direction = "asc") {
  if (!field) return rows;
  const dir = direction === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => dir * compareCellValues(a?.[field], b?.[field]));
}

export { DEFAULT_CURRENCY, compareCellValues, formatDate, formatDateTime, formatTime, formatValue, parseCalendarDate, sortRows, toDateInputValue, toDateTimeInputValue };
